import React from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, UtensilsCrossed, FolderTree, ShoppingBag, Users, Contact2, TicketPercent, BarChart3, LogOut, ShoppingCart, ScrollText, Settings, ShieldCheck, Menu, X, FileText, MapPin, Store, BellRing, ChefHat, KeyRound } from "lucide-react";
import logoLight from "../assets/logo.png";
import { OrderAlertWatcher } from "./OrderAlertWatcher";

const LINKS = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", section: "dashboard" },
  { to: "/admin/products", icon: UtensilsCrossed, label: "Products", section: "products" },
  { to: "/admin/categories", icon: FolderTree, label: "Categories", section: "categories" },
  { to: "/admin/orders", icon: ShoppingBag, label: "Orders", section: "orders" },
  { to: "/admin/notifications", icon: BellRing, label: "Order Alerts", section: "orders" },
  { to: "/admin/delivery-areas", icon: MapPin, label: "Delivery Areas", section: "delivery" },
  { to: "/admin/kitchens", icon: Store, label: "Kitchens", section: "kitchens" },
  { to: "/admin/chefs", icon: ChefHat, label: "Kitchen Chefs", section: "chefs" },
  { to: "/admin/customers", icon: Users, label: "Customers", section: "customers" },
  { to: "/admin/leads", icon: Contact2, label: "CRM / Leads", section: "leads" },
  { to: "/admin/coupons", icon: TicketPercent, label: "Coupons", section: "coupons" },
  { to: "/admin/abandoned", icon: ShoppingCart, label: "Abandoned Carts", section: "abandoned" },
  { to: "/admin/analytics", icon: BarChart3, label: "Analytics", section: "analytics" },
  { to: "/admin/activity", icon: ScrollText, label: "Activity Logs", section: "activity" },
  { to: "/admin/users", icon: ShieldCheck, label: "Admin Users", section: "users" },
  { to: "/admin/security", icon: KeyRound, label: "Password & Security", section: "security" },
  { to: "/admin/content", icon: FileText, label: "Website Content", section: "content" },
  { to: "/admin/settings", icon: Settings, label: "Settings", section: "settings" },
];

const ROLE_PERMISSIONS = {
  super_admin: ["*"],
  manager: ["dashboard", "products", "categories", "orders", "customers", "analytics", "activity", "content", "delivery", "kitchens"],
  order_manager: ["dashboard", "orders"],
  marketing_manager: ["dashboard", "leads", "coupons", "customers", "abandoned", "analytics"],
};

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const ADMIN_ROLES = ["super_admin", "manager", "order_manager", "marketing_manager"];

  React.useEffect(() => {
    if (!loading && (!user || !ADMIN_ROLES.includes(user.role))) {
      navigate(user && user.role === "kitchen_chef" ? "/kitchen" : "/admin/login", { replace: true });
    }
  }, [user, loading]);

  React.useEffect(() => {
    if (!user || !ADMIN_ROLES.includes(user.role)) return;
    const perms = ROLE_PERMISSIONS[user.role] || [];
    if (perms.includes("*")) return;
    const seg = location.pathname.replace(/^\/admin\/?/, "").split("/")[0] || "dashboard";
    if (!perms.includes(seg)) navigate("/admin", { replace: true });
  }, [user, location.pathname]);

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (loading || !user || !ADMIN_ROLES.includes(user.role)) return <div className="min-h-screen flex items-center justify-center bg-cream text-muted-foreground">Loading...</div>;

  const perms = ROLE_PERMISSIONS[user.role] || [];
  const links = LINKS.filter((l) => perms.includes("*") || perms.includes(l.section));

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const Sidebar = (suffix = "") => (
    <>
      <div className="p-5 flex items-center gap-2.5 border-b border-white/10">
        <img src={logoLight} alt="Begum's" className="h-9 w-auto shrink-0" />
        <div><div className="font-serif text-cream text-sm leading-tight">Admin Panel</div><div className="text-[10px] text-gold uppercase tracking-wider">Control Panel</div></div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={`admin-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}${suffix}`}
            className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-gold text-navy font-semibold" : "text-cream/75 hover:bg-white/5 hover:text-gold"}`}>
            <l.icon size={17} /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="text-xs text-gold px-2 capitalize">{user.role?.replace(/_/g, " ")}</div>
        <div className="text-xs text-cream/60 px-2 mb-2 truncate">{user.email}</div>
        <button onClick={doLogout} data-testid={`admin-logout${suffix}`} className="flex items-center gap-2 text-sm text-cream/75 hover:text-destructive px-2"><LogOut size={16} /> Logout</button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-beige" data-testid="admin-layout">
      <OrderAlertWatcher />
      {/* Desktop sidebar (unchanged, >=lg) */}
      <aside className="hidden lg:flex w-60 bg-navy text-cream flex-col fixed inset-y-0 left-0 z-40">
        {Sidebar()}
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 bg-navy text-cream flex items-center justify-between px-4 z-50">
        <button onClick={() => setMobileOpen(true)} data-testid="admin-menu-toggle" className="p-1.5 -ml-1.5 text-cream hover:text-gold"><Menu size={22} /></button>
        <div className="flex items-center gap-2">
          <img src={logoLight} alt="Begum's" className="h-7 w-auto" />
          <span className="font-serif text-sm text-cream">Admin</span>
        </div>
        <button onClick={doLogout} data-testid="admin-logout-mobile" className="p-1.5 -mr-1.5 text-cream/80 hover:text-destructive"><LogOut size={19} /></button>
      </header>

      {/* Mobile off-canvas drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} data-testid="admin-drawer-overlay" />
          <aside className="absolute inset-y-0 left-0 w-64 max-w-[80%] bg-navy text-cream flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
            <button onClick={() => setMobileOpen(false)} data-testid="admin-drawer-close" className="absolute top-4 right-3 text-cream/70 hover:text-gold z-10"><X size={20} /></button>
            {Sidebar("-mobile")}
          </aside>
        </div>
      )}

      <main className="flex-1 lg:ml-60 p-4 pt-[4.5rem] lg:p-8 lg:pt-8 overflow-x-hidden max-w-full"><Outlet /></main>
    </div>
  );
}
